import type { LiveAnnouncer } from '@angular/cdk/a11y';
import { AbstractControl, FormArray } from '@angular/forms';

/**
 * True when the control was activated via keyboard (or a synthetic click from
 * keyboard). Used when callers need to distinguish pointer vs keyboard.
 */
export function isKeyboardActivate(activator?: Event): boolean {
  if (!activator) {
    return false;
  }
  if (activator instanceof KeyboardEvent) {
    return activator.key === ' ' || activator.key === 'Enter';
  }
  return activator instanceof MouseEvent && activator.detail === 0;
}

/** Invalidates in-flight focus restores when a newer move schedules another. */
let moveFocusRestoreGeneration = 0;
let clearPendingMoveFocusGuard: (() => void) | null = null;

/**
 * After a button-driven reorder, DOM recycle drops focus. Refocus the same
 * direction's move button on the moved row (first = earlier/up, second =
 * later/down). If that button is disabled (first/last), use the other one.
 */
export function scheduleRestoreMoveButtonFocus(args: {
  /** Selector for the row/cell that contains `.field-move-actions`. */
  containerSelector: string;
  /** -1 = earlier/up (first button), 1 = later/down (second button) */
  delta: -1 | 1;
}): void {
  const generation = ++moveFocusRestoreGeneration;
  clearPendingMoveFocusGuard?.();
  clearPendingMoveFocusGuard = null;

  const restore = (): boolean => {
    if (generation !== moveFocusRestoreGeneration) {
      return false;
    }
    const allMatches = document.querySelectorAll(args.containerSelector);
    const cell = allMatches[0] as Element | undefined;
    const row = cell?.querySelector('.field-move-actions');
    const buttons = row?.querySelectorAll('button');
    if (!buttons?.length) {
      return false;
    }
    const preferredIndex = args.delta < 0 ? 0 : 1;
    const alternateIndex = args.delta < 0 ? 1 : 0;
    const preferred = buttons[preferredIndex] as HTMLButtonElement | undefined;
    const alternate = buttons[alternateIndex] as HTMLButtonElement | undefined;
    // Never focus a disabled control — browsers drop focus when [disabled] flips on.
    const btn =
      preferred && !preferred.disabled
        ? preferred
        : alternate && !alternate.disabled
          ? alternate
          : null;
    btn?.focus({ preventScroll: true });
    return !!btn && document.activeElement === btn;
  };

  // While DOM recycles the focused button, Space hits <body> and scrolls the page.
  const blockBodySpaceScroll = (e: KeyboardEvent): void => {
    if (e.key !== ' ' && e.key !== 'Enter') {
      return;
    }
    const ae = document.activeElement;
    if (!ae || ae === document.body || ae === document.documentElement) {
      e.preventDefault();
    }
  };
  document.addEventListener('keydown', blockBodySpaceScroll, true);

  const clearGuard = (): void => {
    document.removeEventListener('keydown', blockBodySpaceScroll, true);
    if (clearPendingMoveFocusGuard === clearGuard) {
      clearPendingMoveFocusGuard = null;
    }
  };
  clearPendingMoveFocusGuard = clearGuard;

  // Microtask: best-effort during DOM recycle (Space guard covers the gap).
  // Double rAF: final restore AFTER Angular CD updates [disabled] on edge rows.
  // Newer moves bump generation so stale rAFs from prior tiles cannot steal focus.
  void Promise.resolve().then(() => {
    if (generation !== moveFocusRestoreGeneration) {
      return;
    }
    restore();
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        if (generation !== moveFocusRestoreGeneration) {
          return;
        }
        restore();
        clearGuard();
      });
    });
  });
}

/**
 * Screen-reader status after a successful reorder (matches pick-list wording).
 */
export function announceListItemMoved(args: {
  announcer: LiveAnnouncer;
  label: string;
  /** 0-based index after the move */
  newIndex: number;
  total: number;
  delta: -1 | 1;
  orientation?: 'vertical' | 'horizontal';
}): void {
  const orientation = args.orientation ?? 'vertical';
  const direction =
    orientation === 'horizontal'
      ? args.delta < 0
        ? 'earlier'
        : 'later'
      : args.delta < 0
        ? 'up'
        : 'down';
  const position = args.newIndex + 1;
  void args.announcer.announce(
    `${args.label} moved ${direction} to position ${position} of ${args.total}`,
    'polite',
  );
}

export function announceListItemRemoved(args: {
  announcer: LiveAnnouncer;
  label: string;
}): void {
  void args.announcer.announce(`${args.label} removed`, 'polite');
}

/**
 * Move one FormArray item from previousIndex to currentIndex (CDK drop).
 * Returns the moved control, or null if the indices are invalid / unchanged.
 *
 * Slot / field identity (labels, column ids) must live on the FormGroup so they
 * travel with the row — never derive display numbers from the new index.
 */
export function reorderFormArrayItem(
  formArray: FormArray,
  previousIndex: number,
  currentIndex: number,
): AbstractControl | null {
  if (previousIndex === currentIndex) {
    return null;
  }
  if (
    previousIndex < 0 ||
    currentIndex < 0 ||
    previousIndex >= formArray.length ||
    currentIndex >= formArray.length
  ) {
    return null;
  }

  const moved = formArray.at(previousIndex);
  formArray.removeAt(previousIndex);
  formArray.insert(currentIndex, moved);
  return moved;
}

export interface MoveAnnounceOptions {
  announcer: LiveAnnouncer;
  label: string;
  orientation?: 'vertical' | 'horizontal';
}

/**
 * Step one slot (±1) in a FormArray and optionally restore move-button focus.
 */
export function moveFormArrayItem(args: {
  formArray: FormArray;
  index: number;
  delta: -1 | 1;
  activator?: Event;
  /** Build focus selector from the moved control (e.g. data-move-id). */
  focusContainerSelector?: (moved: AbstractControl) => string | null;
  announce?: MoveAnnounceOptions;
}): boolean {
  const newIndex = args.index + args.delta;
  const moved = reorderFormArrayItem(
    args.formArray,
    args.index,
    newIndex,
  );
  if (!moved) {
    return false;
  }

  if (args.activator && args.focusContainerSelector) {
    const selector = args.focusContainerSelector(moved);
    if (selector) {
      scheduleRestoreMoveButtonFocus({
        containerSelector: selector,
        delta: args.delta,
      });
    }
  }

  if (args.announce) {
    announceListItemMoved({
      announcer: args.announce.announcer,
      label: args.announce.label,
      newIndex,
      total: args.formArray.length,
      delta: args.delta,
      orientation: args.announce.orientation,
    });
  }

  return true;
}
